import dayjs from 'dayjs';
import { TempRecordType, type TempRecordItem } from './types';

/** 与 tempRecord store persist.key 保持一致，跨 Tab 读同一份 localStorage */
export const TEMP_RECORD_PERSIST_KEY = 'pinoTempRecordStore';

type UserRecordsMap = Record<string, Record<string, TempRecordItem>>;

interface PersistedTempRecordState {
  records?: UserRecordsMap;
}

/** 判断一条临时记录当前是否仍有效 */
export function isTempRecordItemValid(item: TempRecordItem | undefined): boolean {
  if (!item) return false;
  if (item.type === TempRecordType.DAILY_RESET && item.expireDate) {
    return dayjs().format('YYYY-MM-DD') <= item.expireDate;
  }
  return true;
}

/** 从 localStorage 读取最新 records（跨 Tab 真相源） */
export function readPersistedTempRecords(): UserRecordsMap {
  try {
    const raw = localStorage.getItem(TEMP_RECORD_PERSIST_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as PersistedTempRecordState;
    return parsed?.records && typeof parsed.records === 'object' ? parsed.records : {};
  } catch {
    return {};
  }
}

function writePersistedTempRecords(records: UserRecordsMap): void {
  try {
    const raw = localStorage.getItem(TEMP_RECORD_PERSIST_KEY);
    const parsed: PersistedTempRecordState = raw ? JSON.parse(raw) : {};
    localStorage.setItem(
      TEMP_RECORD_PERSIST_KEY,
      JSON.stringify({ ...parsed, records }),
    );
  } catch (error) {
    console.error('[tempRecord] 写入跨 Tab 占坑记录失败:', error);
  }
}

function buildRecordItem(value: unknown, type: TempRecordType): TempRecordItem {
  const item: TempRecordItem = {
    value,
    type,
    timestamp: Date.now(),
  };
  if (type === TempRecordType.DAILY_RESET) {
    item.expireDate = dayjs().format('YYYY-MM-DD');
  }
  return item;
}

/**
 * 跨 Tab 原子占坑：以 localStorage 为准，先读后写。
 * 已有有效记录 → false；成功占坑 → true。
 * 有 navigator.locks 时串行化同 key 的竞争，降低多 Tab 同时进首页的竞态。
 */
export async function tryClaimTempRecordInStorage(params: {
  userId: string;
  key: string;
  value: unknown;
  type: TempRecordType;
}): Promise<boolean> {
  const { userId, key, value, type } = params;

  const claim = (): boolean => {
    const records = readPersistedTempRecords();
    const userRecords = { ...(records[userId] || {}) };
    if (isTempRecordItemValid(userRecords[key])) {
      return false;
    }
    userRecords[key] = buildRecordItem(value, type);
    writePersistedTempRecords({ ...records, [userId]: userRecords });
    return true;
  };

  try {
    if (typeof navigator !== 'undefined' && navigator.locks?.request) {
      return await navigator.locks.request(
        `pino-temp-record-claim:${userId}:${key}`,
        claim,
      );
    }
  } catch {
    // locks 不可用或失败时退回无锁占坑
  }
  return claim();
}

/**
 * 监听其它 Tab 对 tempRecord 持久化的写入，回调最新 records。
 * storage 事件不会在写入方自身触发。
 */
export function onTempRecordStorageSync(
  callback: (records: UserRecordsMap) => void,
): () => void {
  if (typeof window === 'undefined') return () => {};

  const handler = (event: StorageEvent) => {
    if (event.key !== TEMP_RECORD_PERSIST_KEY || event.newValue == null) return;
    try {
      const parsed = JSON.parse(event.newValue) as PersistedTempRecordState;
      if (parsed?.records && typeof parsed.records === 'object') {
        callback(parsed.records);
      }
    } catch {
      // ignore malformed payload
    }
  };

  window.addEventListener('storage', handler);
  return () => window.removeEventListener('storage', handler);
}
